import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Barcode,
  CheckCircle2,
  Edit2,
  Package,
  Plus,
  RefreshCw,
  Search,
  Tag,
  Wrench,
  X,
} from "lucide-react";
import { useState } from "react";

import { Button, Card, Money, StatusPill } from "@/components/ui";
import {
  listCategories,
  listProducts,
  updateProduct,
  type CategoryPublic,
  type ProductList,
  type ProductPublic,
  type ProductUpdate,
} from "@/lib/endpoints";
import { ProductModal } from "./ProductModal";

export function ProductsScreen() {
  const queryClient = useQueryClient();

  // Filtros
  const [searchTerm, setSearchTerm] = useState("");
  const [kindFilter, setKindFilter] = useState<"all" | "producto" | "servicio">("all");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("all");
  const [onlyActive, setOnlyActive] = useState(true);

  // Modales
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createKind, setCreateKind] = useState<"producto" | "servicio">("producto");
  const [editingProduct, setEditingProduct] = useState<ProductPublic | null>(null);

  // Queries
  const {
    data: productData,
    isLoading,
    isRefetching,
    refetch,
  } = useQuery<ProductList>({
    queryKey: [
      "products",
      {
        q: searchTerm,
        kind: kindFilter === "all" ? undefined : kindFilter,
        category_id: selectedCategoryId === "all" ? undefined : selectedCategoryId,
        only_active: onlyActive,
      },
    ],
    queryFn: () =>
      listProducts({
        q: searchTerm.trim() || undefined,
        kind: kindFilter === "all" ? undefined : kindFilter,
        category_id: selectedCategoryId === "all" ? undefined : selectedCategoryId,
        only_active: onlyActive,
        limit: 100,
      }),
  });

  const { data: categories = [] } = useQuery<CategoryPublic[]>({
    queryKey: ["categories"],
    queryFn: () => listCategories(false),
  });

  const products = productData?.items ?? [];
  const totalItems = productData?.total ?? 0;

  const totalProducts = products.filter((p) => p.kind === "producto").length;
  const totalServices = products.filter((p) => p.kind === "servicio").length;

  return (
    <div className="p-6 space-y-6">
      {/* Encabezado */}
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-center">
        <div>
          <div className="flex items-center gap-2">
            <h1 className="font-display text-2xl font-bold text-carbon">
              Catálogo de Productos y Servicios
            </h1>
            <StatusPill tone="neutral">{totalItems} registrados</StatusPill>
          </div>
          <p className="mt-1 text-sm text-muted">
            Gestiona los artículos de venta, precios, códigos de barras y control de existencias.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Button
            variant="secondary"
            onClick={() => refetch()}
            disabled={isLoading || isRefetching}
            className="flex items-center gap-1.5"
          >
            <RefreshCw className={`size-4 ${isRefetching ? "animate-spin" : ""}`} />
            Actualizar
          </Button>

          <Button
            variant="secondary"
            onClick={() => {
              setCreateKind("servicio");
              setShowCreateModal(true);
            }}
            className="flex items-center gap-1.5"
          >
            <Wrench className="size-4 text-cyan-600" />
            + Servicio
          </Button>

          <Button
            variant="primary"
            onClick={() => {
              setCreateKind("producto");
              setShowCreateModal(true);
            }}
            className="flex items-center gap-1.5"
          >
            <Plus className="size-4" />
            Nuevo Producto
          </Button>
        </div>
      </div>

      {/* Tarjetas KPI */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Card className="p-4 flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Total en Catálogo
            </span>
            <p className="mt-1 text-2xl font-bold text-carbon">{totalItems}</p>
            <span className="text-xs text-muted">Artículos registrados</span>
          </div>
          <div className="rounded-msr bg-electric/10 p-3 text-electric">
            <Tag className="size-6" />
          </div>
        </Card>

        <Card className="p-4 flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Productos Físicos
            </span>
            <p className="mt-1 text-2xl font-bold text-carbon">{totalProducts}</p>
            <span className="text-xs text-muted">Artículos con inventario</span>
          </div>
          <div className="rounded-msr bg-emerald-500/10 p-3 text-emerald-600">
            <Package className="size-6" />
          </div>
        </Card>

        <Card className="p-4 flex items-center justify-between">
          <div>
            <span className="text-xs font-semibold uppercase tracking-wider text-muted">
              Servicios / Mano de Obra
            </span>
            <p className="mt-1 text-2xl font-bold text-carbon">{totalServices}</p>
            <span className="text-xs text-muted">No requieren stock físico</span>
          </div>
          <div className="rounded-msr bg-cyan-500/10 p-3 text-cyan-600">
            <Wrench className="size-6" />
          </div>
        </Card>
      </div>

      {/* Barra de Filtros y Búsqueda */}
      <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-wrap items-center gap-2">
          {/* Segmented Tipo */}
          <div className="flex items-center gap-1 rounded-msr border border-line bg-snow p-1 text-xs">
            <button
              type="button"
              onClick={() => setKindFilter("all")}
              className={`rounded-msr px-3 py-1.5 font-semibold transition-colors ${
                kindFilter === "all" ? "bg-electric text-white" : "text-muted hover:text-carbon"
              }`}
            >
              Todos
            </button>
            <button
              type="button"
              onClick={() => setKindFilter("producto")}
              className={`rounded-msr px-3 py-1.5 font-semibold transition-colors ${
                kindFilter === "producto" ? "bg-electric text-white" : "text-muted hover:text-carbon"
              }`}
            >
              Productos
            </button>
            <button
              type="button"
              onClick={() => setKindFilter("servicio")}
              className={`rounded-msr px-3 py-1.5 font-semibold transition-colors ${
                kindFilter === "servicio" ? "bg-electric text-white" : "text-muted hover:text-carbon"
              }`}
            >
              Servicios
            </button>
          </div>

          {/* Categoría */}
          <select
            value={selectedCategoryId}
            onChange={(e) => setSelectedCategoryId(e.target.value)}
            className="rounded-msr border border-line bg-snow px-3 py-1.5 text-xs font-medium text-carbon focus:border-electric focus:outline-none"
          >
            <option value="all">Todas las categorías ({categories.length})</option>
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>

          {/* Solo Activos */}
          <label className="flex items-center gap-1.5 text-xs font-medium text-muted cursor-pointer ml-1">
            <input
              type="checkbox"
              checked={onlyActive}
              onChange={(e) => setOnlyActive(e.target.checked)}
              className="rounded border-line text-electric focus:ring-electric"
            />
            Solo activos
          </label>
        </div>

        {/* Búsqueda */}
        <div className="relative w-full sm:w-80">
          <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" />
          <input
            type="text"
            placeholder="Buscar por nombre, SKU o código de barras..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full rounded-msr border border-line bg-snow py-1.5 pl-9 pr-3 text-sm text-carbon placeholder:text-muted focus:border-electric focus:outline-none"
          />
        </div>
      </div>

      {/* Tabla de Artículos */}
      <Card className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-muted">
            <RefreshCw className="size-6 animate-spin mr-2 text-electric" />
            Cargando catálogo de productos...
          </div>
        ) : products.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-center">
            <div className="rounded-full bg-line/60 p-4 mb-3 text-muted">
              <Package className="size-8" />
            </div>
            <h3 className="font-display text-base font-bold text-carbon">
              No se encontraron artículos
            </h3>
            <p className="mt-1 text-xs text-muted max-w-sm">
              {searchTerm
                ? "No hay productos o servicios que coincidan con la búsqueda actual."
                : "Aún no has registrado productos en tu negocio. Agrega el primero para comenzar a vender."}
            </p>
            <Button
              variant="primary"
              size="sm"
              onClick={() => {
                setCreateKind("producto");
                setShowCreateModal(true);
              }}
              className="mt-4 flex items-center gap-1.5"
            >
              <Plus className="size-4" />
              Registrar Primer Producto
            </Button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-left text-sm">
              <thead>
                <tr className="border-b border-line bg-surface text-xs font-semibold uppercase tracking-wider text-muted">
                  <th className="p-3">Tipo</th>
                  <th className="p-3">Artículo</th>
                  <th className="p-3">SKU / Código</th>
                  <th className="p-3">Categoría</th>
                  <th className="p-3">U. Medida</th>
                  <th className="p-3 text-right">Precio Venta</th>
                  <th className="p-3 text-center">Inventario</th>
                  <th className="p-3 text-center">Estado</th>
                  <th className="p-3 text-right">Acción</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line text-carbon">
                {products.map((item) => {
                  const defaultVariant = item.variants?.[0];
                  const primaryBarcode = defaultVariant?.barcodes?.[0]?.barcode;
                  const priceCents = defaultVariant?.price_cents ?? 0;

                  return (
                    <tr key={item.id} className="hover:bg-snow/60 transition-colors">
                      {/* Tipo */}
                      <td className="p-3 whitespace-nowrap">
                        {item.kind === "producto" ? (
                          <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                            <Package className="size-3" /> Producto
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 rounded bg-cyan-50 px-2 py-0.5 text-xs font-semibold text-cyan-700">
                            <Wrench className="size-3" /> Servicio
                          </span>
                        )}
                      </td>

                      {/* Nombre y Marca */}
                      <td className="p-3">
                        <div className="font-semibold text-carbon">{item.name}</div>
                        {item.brand ? (
                          <span className="text-xs text-muted">Marca: {item.brand}</span>
                        ) : null}
                      </td>

                      {/* SKU / Código de barras */}
                      <td className="p-3 whitespace-nowrap">
                        <div className="font-mono text-xs font-semibold text-carbon">
                          {defaultVariant?.sku || "—"}
                        </div>
                        {primaryBarcode ? (
                          <div className="flex items-center gap-1 font-mono text-[11px] text-muted">
                            <Barcode className="size-3" /> {primaryBarcode}
                          </div>
                        ) : null}
                      </td>

                      {/* Categoría */}
                      <td className="p-3 whitespace-nowrap text-xs text-muted">
                        {item.category_name || "—"}
                      </td>

                      {/* Unidad */}
                      <td className="p-3 whitespace-nowrap text-xs text-muted">
                        {item.uom_code || "PZA"}
                      </td>

                      {/* Precio de Venta */}
                      <td className="p-3 whitespace-nowrap text-right font-bold text-carbon">
                        <Money cents={priceCents} />
                      </td>

                      {/* Inventario */}
                      <td className="p-3 whitespace-nowrap text-center">
                        {item.track_inventory ? (
                          <StatusPill tone="ok">Controlado</StatusPill>
                        ) : (
                          <StatusPill tone="neutral">Sin control</StatusPill>
                        )}
                      </td>

                      {/* Estado */}
                      <td className="p-3 whitespace-nowrap text-center">
                        {item.is_active ? (
                          <StatusPill tone="ok">Activo</StatusPill>
                        ) : (
                          <StatusPill tone="danger">Inactivo</StatusPill>
                        )}
                      </td>

                      {/* Acción */}
                      <td className="p-3 whitespace-nowrap text-right">
                        <button
                          type="button"
                          onClick={() => setEditingProduct(item)}
                          className="rounded-msr p-1.5 text-muted hover:bg-line/60 hover:text-carbon transition-colors inline-flex items-center gap-1 text-xs font-semibold"
                          title="Editar producto"
                        >
                          <Edit2 className="size-3.5" />
                          <span className="hidden sm:inline">Editar</span>
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal para Crear Producto o Servicio */}
      <ProductModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        initialKind={createKind}
      />

      {/* Modal para Editar Producto */}
      {editingProduct ? (
        <EditProductModal
          product={editingProduct}
          categories={categories}
          onClose={() => setEditingProduct(null)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ["products"] });
            setEditingProduct(null);
          }}
        />
      ) : null}
    </div>
  );
}

interface EditProductModalProps {
  product: ProductPublic;
  categories: CategoryPublic[];
  onClose: () => void;
  onSuccess: () => void;
}

function EditProductModal({
  product,
  categories,
  onClose,
  onSuccess,
}: EditProductModalProps) {
  const [name, setName] = useState(product.name);
  const [brand, setBrand] = useState(product.brand || "");
  const [description, setDescription] = useState(product.description || "");
  const [categoryId, setCategoryId] = useState(product.category_id || "");
  const [isActive, setIsActive] = useState(product.is_active);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const editMutation = useMutation({
    mutationFn: async () => {
      const cleanName = name.trim();
      if (!cleanName) {
        throw new Error("El nombre no puede estar vacío.");
      }
      const payload: ProductUpdate = {
        name: cleanName,
        brand: brand.trim() || null,
        description: description.trim() || null,
        category_id: categoryId || null,
        is_active: isActive,
      };
      await updateProduct(product.id, payload);
    },
    onSuccess: () => {
      onSuccess();
    },
    onError: (err: Error) => {
      setErrorMessage(err.message || "Error al actualizar el producto");
    },
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
      <Card className="w-full max-w-lg p-6 space-y-4">
        <div className="flex items-center justify-between border-b border-line pb-3">
          <div className="flex items-center gap-2">
            <Edit2 className="size-5 text-electric" />
            <h2 className="font-display text-lg font-bold text-carbon">Editar Artículo</h2>
          </div>
          <button type="button" onClick={onClose} className="text-muted hover:text-carbon">
            <X className="size-5" />
          </button>
        </div>

        {errorMessage ? (
          <div className="rounded-msr bg-rose-50 border border-rose-200 p-3 text-xs text-rose-800">
            {errorMessage}
          </div>
        ) : null}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            editMutation.mutate();
          }}
          className="space-y-3.5 text-sm text-carbon"
        >
          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Nombre *</label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-muted mb-1">Marca</label>
              <input
                type="text"
                value={brand}
                onChange={(e) => setBrand(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
              />
            </div>

            <div>
              <label className="block text-xs font-semibold text-muted mb-1">Categoría</label>
              <select
                value={categoryId}
                onChange={(e) => setCategoryId(e.target.value)}
                className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-xs font-medium text-carbon focus:border-electric focus:outline-none"
              >
                <option value="">(Sin categoría)</option>
                {categories.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted mb-1">Descripción</label>
            <textarea
              rows={2}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full rounded-msr border border-line bg-snow px-3 py-2 text-sm text-carbon focus:border-electric focus:outline-none"
            />
          </div>

          <div className="pt-1">
            <label className="flex items-center gap-2 text-xs font-semibold text-carbon cursor-pointer">
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                className="rounded border-line text-electric focus:ring-electric"
              />
              Artículo activo para venta en POS y catálogos
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-line pt-3">
            <Button type="button" variant="secondary" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" loading={editMutation.isPending} className="flex items-center gap-1.5">
              <CheckCircle2 className="size-4" />
              Guardar Cambios
            </Button>
          </div>
        </form>
      </Card>
    </div>
  );
}
